/**
 * Client-side image compression. Downscales so (width + height) ≤ MAX_DIM_SUM
 * and re-encodes as JPEG to keep stored size (and, for E2EE, the encrypted blob)
 * small. Runs before any encryption.
 */

export interface CompressedImage {
  blob: Blob;
  bytes: Uint8Array;
  width: number;
  height: number;
  mime: string;
}

/**
 * Combined width+height cap, in pixels. No server-side dimension cap exists
 * (MediaController only enforces a byte-size cap — see MAX_IMAGE_BYTES in
 * PhotoUploadSheet); a q0.82 JPEG at 4000px sum stays well under it.
 */
export const MAX_DIM_SUM = Number(import.meta.env.VITE_MAX_DIM_SUM) || 4000;
const QUALITY = Number(import.meta.env.VITE_QUALITY) || 0.82;

/** AI Chatbot image compression settings. */
export const AI_IMAGE_MAX_DIM_SUM = Number(import.meta.env.VITE_AI_IMAGE_MAX_DIM_SUM) || 1500;
export const AI_IMAGE_QUALITY = Number(import.meta.env.VITE_AI_IMAGE_QUALITY) || 0.8;

const OUTPUT_MIME = 'image/jpeg';

/**
 * Load, downscale, and re-encode an image File. If the source already fits and
 * is a JPEG we still re-encode (strips EXIF/orientation surprises and normalises
 * size); callers pass plain image Files only.
 */
export async function compressImage(
  file: File,
  maxDimSum = MAX_DIM_SUM,
  quality = QUALITY,
): Promise<CompressedImage> {
  const bitmap = await loadBitmap(file);
  try {
    const { width, height } = scaleToSum(bitmap.width, bitmap.height, maxDimSum);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable.');
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await canvasToBlob(canvas, OUTPUT_MIME, quality);
    const bytes = new Uint8Array(await blob.arrayBuffer());
    return { blob, bytes, width, height, mime: OUTPUT_MIME };
  } finally {
    if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close();
  }
}

/** Compute target dimensions so w+h ≤ maxSum, preserving aspect ratio. */
function scaleToSum(w: number, h: number, maxSum: number): { width: number; height: number } {
  const sum = w + h;
  if (sum <= maxSum) return { width: w, height: h };
  const factor = maxSum / sum;
  return {
    width: Math.max(1, Math.round(w * factor)),
    height: Math.max(1, Math.round(h * factor)),
  };
}

async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  // Prefer createImageBitmap (handles EXIF orientation when supported).
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
    } catch {
      /* fall through to <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Could not load image.'));
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Image encoding failed.'))),
      mime,
      quality,
    );
  });
}
